import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BlogCard from '../components/BlogCard';
import CTABanner from '../components/CTABanner';
import { ArrowLeft, Clock, Tag, Calendar, Share2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        setPost(res.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, url }); } catch {}
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex justify-center items-center pt-40 pb-20">
          <Loader2 className="animate-spin text-[#003366]" size={48} />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-40 pb-20 text-center px-6">
          <h1 className="text-3xl font-bold text-[#003366] mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The blog post you're looking for doesn't exist or has been removed.</p>
          <button onClick={() => navigate('/blog')} className="text-[#003366] font-semibold hover:underline flex items-center justify-center mx-auto">
            <ArrowLeft size={18} className="mr-2" /> Back to Blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const date = new Date(post.published_at).toLocaleDateString('en-CA', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{post.title} - WorkNation Labour Ltd Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <link rel="canonical" href={`https://worknation.ca/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.meta_description || post.excerpt,
          "author": { "@type": "Organization", "name": post.author },
          "publisher": { "@type": "Organization", "name": "WorkNation Labour Ltd" },
          "datePublished": post.published_at,
          "articleSection": post.category,
        })}</script>
      </Helmet>
      <Header />
      <main>
        <article className="pt-28 pb-16 px-6">
          <div className="max-w-3xl mx-auto">
            {/* Back link */}
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center text-sm text-gray-500 hover:text-[#003366] mb-6 transition-colors duration-200"
              data-testid="back-to-blog"
            >
              <ArrowLeft size={16} className="mr-1" /> Back to Blog
            </button>

            {/* Article header */}
            <header className="mb-8">
              {/* Hero image */}
              {post.image_url && (
                <div className="w-full h-64 sm:h-80 rounded-xl overflow-hidden mb-6">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                  <Tag size={12} className="mr-1" aria-hidden="true" />
                  {post.category}
                </span>
                <span className="flex items-center text-sm text-gray-400">
                  <Clock size={14} className="mr-1" aria-hidden="true" />
                  {post.reading_time} min read
                </span>
                <span className="flex items-center text-sm text-gray-400">
                  <Calendar size={14} className="mr-1" aria-hidden="true" />
                  {date}
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold text-[#003366] leading-tight mb-4" data-testid="blog-post-title">
                {post.title}
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                {post.excerpt}
              </p>

              <div className="flex items-center justify-between border-t border-b border-gray-100 py-3">
                <span className="text-sm text-gray-500">By <strong className="text-[#003366]">{post.author}</strong></span>
                <button
                  onClick={handleShare}
                  className="flex items-center text-sm text-gray-500 hover:text-[#003366] transition-colors duration-200"
                  data-testid="share-btn"
                  aria-label="Share this article"
                >
                  <Share2 size={16} className="mr-1" /> Share
                </button>
              </div>
            </header>

            {/* Article body */}
            <div
              className="prose prose-lg prose-slate max-w-none
                prose-headings:text-[#003366] prose-headings:font-bold
                prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-[#003366] prose-a:font-semibold hover:prose-a:underline
                prose-li:text-gray-700 prose-ul:my-4 prose-ol:my-4
                prose-strong:text-[#003366]"
              dangerouslySetInnerHTML={{ __html: post.content }}
              data-testid="blog-post-content"
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-10 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-500 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm hover:bg-gray-200 transition-colors duration-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        {/* Related Posts */}
        {post.related_posts && post.related_posts.length > 0 && (
          <section className="py-12 px-6 bg-gray-50">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl font-bold text-[#003366] mb-8 text-center">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {post.related_posts.map((rp) => (
                  <BlogCard key={rp.id} post={rp} />
                ))}
              </div>
            </div>
          </section>
        )}

        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
